import type { EntryValueViewT } from "@patient-watch/types";
import type { InputKeyT } from "@patient-watch/types";
import { AlertTriangle, Phone } from "lucide-react";
import { resolveInputKey } from "@/features/pages/utils/inputKeyAliases";
import {
    BranchingScaleOptionsEntryValue,
    CheckboxEntryValue,
    CountEntryValue,
    DatePickerEntryValue,
    DateTimePickerEntryValue,
    DefaultEntryValue,
    DocumentUploadEntryValue,
    DurationEntryValue,
    FootPhotosEntryValue,
    GlucoseSliderEntryValue,
    LongTextEntryValue,
    MultipleChoiceEntryValue,
    MeasurementEntryValue,
    NumberEntryValue,
    PainSliderEntryValue,
    PhotoEntryValue,
    RatingsListEntryValue,
    RepeatableGroupEntryValue,
    ScaleRatingEntryValue,
    SliderEntryValue,
    StarEntryValue,
    YesNoEntryValue,
} from "./input-types";
import { MonthYearEntryValue } from "./input-types/MonthYearEntryValue";
import TriedTreatmentEntryValue from "./input-types/TriedTreatmentEntryValue";
import { choiceEntryValueLabel, choiceEntryValueLabels, isOptionId } from "../choiceEntryValueLabel";
import { useOwnOptions } from "@/features/dictionaries/api/selectOwnOptions";
import { useMemo } from "react";
import { entryValueUnit } from "../measurementRuns";

// Safety Risk Warning Component for PHQ-9 HIGH RISK flag
function SafetyRiskWarning() {
    return (
        <div className="bg-red-50 p-4 border-2 border-red-500 rounded-lg">
            <div className="flex items-start gap-3">
                <AlertTriangle className="flex-shrink-0 mt-0.5 w-6 h-6 text-red-600" />
                <div className="space-y-3">
                    <div>
                        <h4 className="font-bold text-red-800 text-lg">Safety Risk Assessment: HIGH RISK</h4>
                        <p className="mt-1 text-red-700 text-sm">
                            This patient has indicated frequent thoughts of self-harm or suicide. Immediate clinical
                            follow-up is recommended.
                        </p>
                    </div>
                    <div className="bg-white p-3 rounded-md">
                        <h5 className="flex items-center gap-2 mb-2 font-semibold text-red-800 text-sm">
                            <Phone size={16} />
                            Crisis Resources
                        </h5>
                        <ul className="space-y-1 text-red-700 text-sm">
                            <li>
                                <strong>Samaritans:</strong> 116 123 (free, 24/7)
                            </li>
                            <li>
                                <strong>NHS Crisis Line:</strong> 111, then select mental health
                            </li>
                            <li>
                                <strong>SHOUT:</strong> Text "SHOUT" to 85258
                            </li>
                            <li>
                                <strong>Emergency:</strong> 999 if in immediate danger
                            </li>
                        </ul>
                    </div>
                    <p className="text-red-600 text-xs">
                        Please ensure this patient receives appropriate clinical support as soon as possible.
                    </p>
                </div>
            </div>
        </div>
    );
}

// Extended interface for properties that might not be in the base type
type ExtendedEntryValue = EntryValueViewT & {
    source?: string;
    unit?: string;
    duration_time_unit?: string;
    json_value?: Array<
        Array<{
            input_label: string;
            input_id: string;
            input_key: InputKeyT;
            value: string | number | boolean | string[] | Date | null;
        }>
    >;
    min_value?: number | null;
    max_value?: number | null;
    /** Nested `inputs` row from `entry_values_view` (carries the input's slider range). */
    inputs?: { min_value?: number | null; max_value?: number | null; unit?: string | null } | null;
};

/** Fill percentage for a slider value relative to its input's own min/max range (defaults 0–100). */
function sliderPercentage(entryValue: ExtendedEntryValue): number {
    const value = Number(entryValue.value);
    if (!Number.isFinite(value)) return 0;
    const min = entryValue.min_value ?? entryValue.inputs?.min_value ?? 0;
    const max = entryValue.max_value ?? entryValue.inputs?.max_value ?? 100;
    if (max <= min) return 0;
    return Math.min(100, Math.max(0, (100 * (value - min)) / (max - min)));
}

/** Labels with any own-option id still showing swapped for its text, fetched when the answer has no linked option. */
function useChoiceLabels(labels: string[]): string[] {
    const ids = useMemo(() => labels.filter(isOptionId), [labels]);
    const ownOptions = useOwnOptions(ids);
    return useMemo(
        () => labels.map((label) => ownOptions.find((option) => option.option_id === label)?.label ?? label),
        [labels, ownOptions]
    );
}

interface ChoiceProps {
    entryValue: EntryValueViewT;
    size?: "sm" | "md" | "lg";
    className?: string;
}

function SingleChoiceValue({ entryValue, size, className }: ChoiceProps) {
    const stored = choiceEntryValueLabel(entryValue);
    const labels = useMemo(() => [stored], [stored]);
    const [label] = useChoiceLabels(labels);
    return (
        <DefaultEntryValue
            value={label ?? null}
            inputLabel={entryValue.input_label}
            size={size}
            className={className}
        />
    );
}

function MultipleChoiceValue({ entryValue, size, className }: ChoiceProps) {
    const stored = useMemo(() => choiceEntryValueLabels(entryValue), [entryValue]);
    const labels = useChoiceLabels(stored);
    return (
        <MultipleChoiceEntryValue
            value={labels}
            inputLabel={entryValue.input_label}
            size={size}
            className={className}
        />
    );
}

interface Props {
    entryValue: EntryValueViewT;
    size?: "sm" | "md" | "lg";
    className?: string;
    diaryId?: string;
}

export function EntryValueSwitch({ entryValue, size, className, diaryId }: Props) {
    // Cast to extended type to access optional properties
    const extendedEntryValue = entryValue as ExtendedEntryValue;
    const inputKey = entryValue.input_key;

    switch (resolveInputKey(inputKey)) {
        case "warning":
            // Display warning alerts for critical clinical notifications
            // Currently supports HIGH RISK flag for PHQ-9 suicidal ideation
            if (entryValue.text_value === "HIGH RISK") {
                return <SafetyRiskWarning />;
            }
            // For other warning types, fall through to default
            return (
                <DefaultEntryValue
                    value={(entryValue.text_value as string) ?? "Warning"}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "repeatable-group":
            return (
                <RepeatableGroupEntryValue
                    value={entryValue.value as string}
                    jsonValue={
                        extendedEntryValue.json_value as Array<
                            Array<{
                                input_label: string;
                                input_id: string;
                                input_key: InputKeyT;
                                value: string | number | boolean | string[] | Date | null;
                            }>
                        >
                    }
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "count":
            return (
                <CountEntryValue
                    value={entryValue.number_value as number}
                    unit={entryValueUnit(extendedEntryValue)}
                    inputLabel={
                        entryValueUnit(extendedEntryValue)
                            ? entryValue.page_label?.trim() || entryValue.input_label
                            : entryValue.input_label
                    }
                    size={size}
                    className={className}
                />
            );
        case "number":
            return (
                <NumberEntryValue
                    value={entryValue.number_value as number}
                    unit={entryValueUnit(extendedEntryValue)}
                    maxScore={entryValue.max_score}
                    inputLabel={
                        entryValueUnit(extendedEntryValue)
                            ? entryValue.page_label?.trim() || entryValue.input_label
                            : entryValue.input_label
                    }
                    size={size}
                    className={className}
                />
            );
        case "single-choice":
            return <SingleChoiceValue entryValue={entryValue} size={size} className={className} />;
        case "measurement":
            return (
                <MeasurementEntryValue
                    value={entryValue.number_value as number | null}
                    unit={entryValue.text_value}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "visa-a-8":
        case "visa-g-8":
        case "visa-h-8":
        case "visa-p-8":
            return (
                <BranchingScaleOptionsEntryValue
                    value={
                        typeof entryValue.number_value === "number"
                            ? entryValue.number_value
                            : entryValue.value !== null
                              ? Number(entryValue.value)
                              : null
                    }
                    textValue={entryValue.text_value}
                    inputId={entryValue.input_id}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "long-text":
            return (
                <LongTextEntryValue
                    value={(entryValue.value as string) ?? "N/A"}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "duration":
            return (
                <DurationEntryValue
                    value={entryValue.value as number}
                    durationTimeUnit={extendedEntryValue.duration_time_unit}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "slider":
            return (
                <SliderEntryValue
                    percentageValue={sliderPercentage(extendedEntryValue)}
                    value={entryValue.value as number}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "date-picker":
            return (
                <DatePickerEntryValue
                    value={entryValue.value as string}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "date-time-picker":
            return (
                <DateTimePickerEntryValue
                    value={entryValue.value as string}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "month-year":
            return (
                <MonthYearEntryValue
                    value={entryValue.value as string}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "multiple-choice":
            return <MultipleChoiceValue entryValue={entryValue} size={size} className={className} />;
        case "ms-adl-selection":
            return (
                <MultipleChoiceEntryValue
                    value={entryValue.value as string[]}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                    localiseMsAdlActivities
                />
            );
        case "photo":
            return (
                <PhotoEntryValue
                    value={entryValue.value as string}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "file":
            return (
                <DocumentUploadEntryValue
                    diaryId={diaryId}
                    entryId={entryValue.entry_id}
                    inputId={entryValue.input_id}
                    fileName={entryValue.text_value}
                    objectId={entryValue.object_id}
                    signedUrl={entryValue.signed_url}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "scale-rating":
            return (
                <ScaleRatingEntryValue
                    value={entryValue.number_value === null ? null : entryValue.number_value}
                    textValue={entryValue.text_value}
                    inputId={entryValue.input_id}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "ratings-list":
            return (
                <RatingsListEntryValue
                    value={entryValue.json_value as unknown as Record<string, number>}
                    size={size}
                    className={className}
                />
            );
        case "ms-adl-ratings-list":
            return (
                <RatingsListEntryValue
                    value={entryValue.json_value as unknown as Record<string, number>}
                    size={size}
                    className={className}
                    localiseMsAdlActivities
                />
            );
        case "foot-photos":
            return (
                <FootPhotosEntryValue
                    value={entryValue.value as string}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "pain-slider":
            return (
                <PainSliderEntryValue
                    value={entryValue.value as number}
                    size={size}
                    inputLabel={entryValue.input_label}
                    className={className}
                />
            );
        case "glucose-slider":
            return (
                <GlucoseSliderEntryValue
                    value={entryValue.value as number}
                    size={size}
                    inputLabel={entryValue.input_label}
                    className={className}
                />
            );
        case "star":
            return (
                <StarEntryValue
                    value={entryValue.number_value ?? (entryValue.value as string | null)}
                    inputId={entryValue.input_id}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "yes-no":
            return (
                <YesNoEntryValue
                    value={entryValue.value as string}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "checkbox":
            return (
                <CheckboxEntryValue
                    value={entryValue.value as boolean | string}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
        case "tried-treatment":
            return (
                <TriedTreatmentEntryValue
                    value={
                        entryValue.json_value as unknown as {
                            hasTried: boolean;
                            duration: string;
                            isOngoing?: string;
                        }
                    }
                    inputLabel={entryValue.input_label}
                    className={className}
                />
            );
        default:
            return (
                <DefaultEntryValue
                    value={(entryValue.value as string) ?? "N/A"}
                    inputLabel={entryValue.input_label}
                    size={size}
                    className={className}
                />
            );
    }
}
